import { Box, FormControlLabel, GridLegacy as Grid, styled, Switch, Tooltip, Typography, useTheme } from '@mui/material'
import { FormSwitchOption } from 'ComponentLibrary/src/atoms/forms/FormSwitches/types'
import { CSSProperties } from 'react'
import { Info } from 'react-feather'
import { Control, Controller, FieldValues } from 'react-hook-form'

type FormSwitchesProps<TFieldValues extends FieldValues> = {
    options: FormSwitchOption<TFieldValues>[]
    fullWidth?: boolean
    row?: boolean
    rowSpacing?: number
    control: Control<TFieldValues>
    xsForToggleSwitch?: number
    style?: CSSProperties
}

const FormSwitches = <TFieldValues extends FieldValues>({
    options,
    fullWidth,
    control,
    rowSpacing,
    xsForToggleSwitch: toggleXS,
    style,
}: FormSwitchesProps<TFieldValues>) => {
    const theme = useTheme()
    return (
        <>
            {options.map(({ label, secondaryLabel, formInputName, error, disabled, validate, resetField, setIsChecked }) => (
                <Grid item xs={toggleXS ? toggleXS : fullWidth ? 12 : 6} key={formInputName} paddingLeft="12px" style={style}>
                    <Controller
                        control={control}
                        name={formInputName}
                        rules={{ validate }}
                        render={({ field: { value: valueProp, onChange, ...fieldProps } }) => {
                            return (
                                <>
                                    <FormControlLabel
                                        sx={{
                                            color: error ? theme.palette.danger.main : undefined,
                                        }}
                                        control={
                                            <Switch
                                                checked={valueProp || false}
                                                disabled={disabled}
                                                onChange={(event) => {
                                                    setIsChecked?.(event.target.checked)
                                                    resetField?.(event.target.checked)
                                                    onChange(event)
                                                }}
                                                disableRipple
                                                {...fieldProps}
                                            />
                                        }
                                        label={
                                            <Box display="flex">
                                                <Typography>{label}</Typography>
                                                {secondaryLabel && (
                                                    <Typography color={theme.palette.text.secondary} marginLeft="8px">
                                                        {secondaryLabel}
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                    />
                                </>
                            )
                        }}
                    />
                    {error?.message && (
                        <Tooltip title={error.message} role="alert">
                            <IconWrapper rowSpacing={rowSpacing}>
                                <Info size={20} color={theme.palette.error.main} />
                            </IconWrapper>
                        </Tooltip>
                    )}
                </Grid>
            ))}
        </>
    )
}

const IconWrapper = styled('div')<{ rowSpacing?: number }>(({ theme, rowSpacing }) => ({
    position: 'absolute',
    right: rowSpacing ? theme.spacing(rowSpacing / 2) : theme.spacing(-4),
    top: theme.spacing(2.2),
}))

export default FormSwitches
